/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import React, { useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl, FormattedMessage } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { useAppSelector } from '@ibm-watson/shared/store';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  selectModelsFilteredByAssistantLanguage,
  selectNonDeprecatedSpeechToTextModels,
  selectSpeechToTextModel,
  selectSpeechToTextFilteredModel,
  selectSpeechToTextInstanceLoaded,
  selectDefaultSpeechToTextModel
} from '../../../store/voice/selectors';
import { ChannelContentProps } from '../../../util/types';
import { NARROWBAND, BROADBAND, TELEPHONY } from '../constants';
import { FIELDS } from '../schema';
import { ControlledDropdownSelector } from '../speech-common/ControlledDropdownSelector';
import { ControlledToggle } from '../speech-common/ControlledToggle';
import { SpeechToText as messages } from './messages';

export const Models = ({ readOnly }: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const [filtered, setFiltered] = useState(true);
  const [field, setField] = useState(
    getFieldValue(FIELDS.STT.BAND_PREFERENCE) === TELEPHONY ||
      getFieldValue(FIELDS.STT.BAND_PREFERENCE) === NARROWBAND
      ? FIELDS.STT.NARROWBAND.MODEL
      : FIELDS.STT.BROADBAND.MODEL
  );
  const [models, setModels] = useState([]);
  const [noFilteredModels, setNoFilteredModels] = useState(false);
  const [defaultValue] = useState(getFieldValue(field));

  const allModels = useAppSelector(
    selectNonDeprecatedSpeechToTextModels,
    shallowEqual
  );

  const filteredModels = useAppSelector(
    selectModelsFilteredByAssistantLanguage,
    shallowEqual
  );
  const isSttLoaded = useAppSelector(
    selectSpeechToTextInstanceLoaded,
    shallowEqual
  );

  const modelInFiltered = useAppSelector(
    state => selectSpeechToTextFilteredModel(state, getFieldValue(field)),
    shallowEqual
  );
  const modelInAllVoices = useAppSelector(
    state => selectSpeechToTextModel(state, getFieldValue(field)),
    shallowEqual
  );
  const defaultSttModel = useAppSelector(
    selectDefaultSpeechToTextModel,
    shallowEqual
  );

  const defaultModelInFiltered = useAppSelector(
    state => selectSpeechToTextFilteredModel(state, defaultSttModel),
    shallowEqual
  );
  // Set the field value based on selection
  // Compare against original to determine if dirty
  const handleSelection = useCallback(
    selectedItem => {
      if (/telephony/i.test(selectedItem.name)) {
        setField(FIELDS.STT.NARROWBAND.MODEL);
        updateFieldValue(FIELDS.STT.BAND_PREFERENCE, TELEPHONY);
        updateFieldValue(FIELDS.STT.NARROWBAND.MODEL, selectedItem.name);
      } else if (/narrowband/i.test(selectedItem.name)) {
        setField(FIELDS.STT.NARROWBAND.MODEL);
        updateFieldValue(FIELDS.STT.BAND_PREFERENCE, NARROWBAND);
        updateFieldValue(FIELDS.STT.NARROWBAND.MODEL, selectedItem.name);
      } else if (/broadband|multimedia/i.test(selectedItem.name)) {
        setField(FIELDS.STT.BROADBAND.MODEL);
        updateFieldValue(FIELDS.STT.BAND_PREFERENCE, BROADBAND);
        updateFieldValue(FIELDS.STT.BROADBAND.MODEL, selectedItem.name);
      } else {
        // For Large speech models, names are en-US, en-GB, etc this will handle the case
        setField(FIELDS.STT.NARROWBAND.MODEL);
        updateFieldValue(FIELDS.STT.BAND_PREFERENCE, TELEPHONY);
        updateFieldValue(FIELDS.STT.NARROWBAND.MODEL, selectedItem.name);
      }
    },
    [updateFieldValue]
  );

  const getIsFiltered = (): boolean =>
    getFieldValue(FIELDS.STT.LANGUAGE_SELECTIONS_FILTER_TOGGLE) !== 'NO';

  const updateIsFiltered = (isFiltered: boolean) => {
    setFiltered(isFiltered);
    updateFieldValue(
      FIELDS.STT.LANGUAGE_SELECTIONS_FILTER_TOGGLE,
      isFiltered ? 'YES' : 'NO'
    );
  };

  useEffect(() => {
    //get default/user selected value for toggle
    let isFiltered = getIsFiltered();
    if (isSttLoaded && !modelInAllVoices) {
      isFiltered = defaultModelInFiltered ? true : false;
      updateIsFiltered(isFiltered);
      updateFieldValue(field, defaultSttModel);
    }
    updateIsFiltered(isFiltered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSttLoaded]);

  useEffect(() => {
    //get default/user selected value for toggle
    let isFiltered: boolean = getIsFiltered();
    setModels(isFiltered ? filteredModels : allModels);

    if (allModels.length !== 0) {
      if (filteredModels.length === 0) {
        isFiltered = false;
        setNoFilteredModels(true);
      } else {
        setNoFilteredModels(false);

        if (isFiltered && !modelInFiltered && getFieldValue(field)) {
          updateFieldValue(field, '');
        }
      }
    }
    setFiltered(isFiltered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allModels,
    field,
    filtered,
    filteredModels,
    getFieldValue,
    modelInFiltered,
    updateFieldValue
  ]);

  return (
    <>
      <div className="TabContent__body-content">
        <div className="TabContent__field-container">
          <div className="Dropdown__filtered-container">
            <div className="Dropdown__filtered-text">
              <h5>
                <FormattedMessage {...messages.filterModel} />
              </h5>
              <p className="TabContent__inner-body">
                <FormattedMessage {...messages.filterWarning} />
              </p>
            </div>
            <div className="Dropdown__filtered-toggle">
              <ControlledToggle
                id="model-filter-toggle"
                toggled={filtered}
                disabled={noFilteredModels}
                instanceLoaded={isSttLoaded}
                onToggle={() => {
                  updateIsFiltered(!filtered);
                }}
              />
            </div>
          </div>
          <div className="TabContent__spacer16" />
        </div>
        <div className="TabContent__field-container">
          <h5>
            <FormattedMessage {...messages.languageModel} />
          </h5>
          <p className="TabContent__inner-body">
            <FormattedMessage {...messages.languageDescription} />
          </p>
          <div className="TabContent__spacer16" />
          <div className="Dropdown__filtered-selector">
            <ControlledDropdownSelector
              id="stt-models"
              readOnly={readOnly}
              labelText={t(messages.languageModel)}
              defaultValue={defaultValue}
              field={field}
              items={models}
              onChange={handleSelection}
              instanceLoaded={isSttLoaded}
            />
          </div>
        </div>
      </div>
    </>
  );
};
